/**
 * DriverHomePage - Pagina inicial do App do Motorista
 *
 * Funcionalidades:
 * - Resumo do dia (entregas pendentes, concluidas)
 * - Proxima parada com navegacao rapida
 * - Alertas pendentes
 * - Acesso rapido a funcoes
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Chip,
  LinearProgress,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Navigation as NavigateIcon,
  CheckCircle as CompleteIcon,
  Schedule as PendingIcon,
  LocalShipping as VehicleIcon,
  Assignment as TaskIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useEffectAsync } from '../reactHelper';
import driverAssignmentService, { ASSIGNMENT_STATUS } from '../routing/services/driverAssignmentService';

// Componente de card de estatistica
const StatCard = ({ icon: Icon, label, value, color, onClick, tokens }) => (
  <Card
    sx={{
      backgroundColor: tokens?.surface?.raised,
      borderRadius: tokens?.radius?.lg,
      border: `1px solid ${tokens?.border?.subtle}`,
    }}
  >
    <CardActionArea onClick={onClick} sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: tokens?.radius?.md,
            backgroundColor: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ color, fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            sx={{
              color: tokens?.text?.primary,
              fontWeight: 700,
              fontSize: '24px',
              lineHeight: 1,
            }}
          >
            {value}
          </Typography>
          <Typography
            sx={{
              color: tokens?.text?.tertiary,
              fontSize: '13px',
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </CardActionArea>
  </Card>
);

// Componente de rota ativa
const ActiveRouteCard = ({ route, onNavigate, onViewDetails, tokens }) => {
  const progress = route.progress || 0;
  const nextStop = route.nextStop;

  return (
    <Card
      sx={{
        backgroundColor: tokens?.surface?.raised,
        borderRadius: tokens?.radius?.lg,
        border: `1px solid ${tokens?.accent?.primary}`,
        overflow: 'hidden',
      }}
    >
      {/* Header com progresso */}
      <Box
        sx={{
          backgroundColor: `${tokens?.accent?.primary}15`,
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography
            sx={{
              color: tokens?.text?.primary,
              fontWeight: 600,
              fontSize: '15px',
            }}
          >
            {route.name || 'Rota em Execucao'}
          </Typography>
          <Chip
            label="Em Andamento"
            size="small"
            sx={{
              backgroundColor: tokens?.semantic?.success,
              color: '#fff',
              fontWeight: 600,
              fontSize: '10px',
              height: 22,
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              flex: 1,
              height: 6,
              borderRadius: 3,
              backgroundColor: tokens?.surface?.base,
              '& .MuiLinearProgress-bar': {
                backgroundColor: tokens?.accent?.primary,
                borderRadius: 3,
              },
            }}
          />
          <Typography sx={{ color: tokens?.text?.secondary, fontSize: '12px', fontWeight: 600 }}>
            {Math.round(progress)}%
          </Typography>
        </Box>
      </Box>

      {/* Proxima parada */}
      {nextStop && (
        <CardContent sx={{ p: 2 }}>
          <Typography
            sx={{
              color: tokens?.text?.tertiary,
              fontSize: '11px',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              mb: 0.5,
            }}
          >
            Proxima Parada
          </Typography>
          <Typography
            sx={{
              color: tokens?.text?.primary,
              fontWeight: 600,
              fontSize: '16px',
              mb: 0.5,
            }}
          >
            {nextStop.name}
          </Typography>
          <Typography
            sx={{
              color: tokens?.text?.secondary,
              fontSize: '13px',
              mb: 2,
            }}
          >
            {nextStop.address}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<NavigateIcon />}
              onClick={() => onNavigate(nextStop)}
              sx={{
                backgroundColor: tokens?.accent?.primary,
                '&:hover': { backgroundColor: tokens?.accent?.hover },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Navegar
            </Button>
            <Button
              variant="outlined"
              onClick={onViewDetails}
              sx={{
                borderColor: tokens?.border?.default,
                color: tokens?.text?.secondary,
                textTransform: 'none',
                minWidth: 48,
              }}
            >
              <ArrowIcon />
            </Button>
          </Box>
        </CardContent>
      )}
    </Card>
  );
};

// Componente de rota pendente de aceite
const PendingRouteCard = ({ route, onAccept, onReject, tokens }) => (
  <Card
    sx={{
      backgroundColor: tokens?.surface?.raised,
      borderRadius: tokens?.radius?.lg,
      border: `1px solid ${tokens?.semantic?.warning}`,
    }}
  >
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Chip
            icon={<PendingIcon sx={{ fontSize: '14px !important' }} />}
            label="Aguardando Aceite"
            size="small"
            sx={{
              backgroundColor: `${tokens?.semantic?.warning}20`,
              color: tokens?.semantic?.warning,
              fontWeight: 600,
              fontSize: '10px',
              mb: 1,
            }}
          />
          <Typography
            sx={{
              color: tokens?.text?.primary,
              fontWeight: 600,
              fontSize: '16px',
            }}
          >
            {route.name}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box>
          <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px' }}>
            Paradas
          </Typography>
          <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600 }}>
            {route.stopsCount || 0}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px' }}>
            Distancia
          </Typography>
          <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600 }}>
            {route.distance ? `${(route.distance / 1000).toFixed(1)} km` : '-'}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '11px' }}>
            Duracao Est.
          </Typography>
          <Typography sx={{ color: tokens?.text?.primary, fontWeight: 600 }}>
            {route.duration ? `${Math.round(route.duration / 60)} min` : '-'}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={() => onAccept(route)}
          sx={{
            backgroundColor: tokens?.semantic?.success,
            '&:hover': { backgroundColor: '#16a34a' },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Aceitar
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={() => onReject(route)}
          sx={{
            borderColor: tokens?.semantic?.error,
            color: tokens?.semantic?.error,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Recusar
        </Button>
      </Box>
    </CardContent>
  </Card>
);

const DriverHomePage = () => {
  const tokens = usePremiumTokens();
  const navigate = useNavigate();

  // Estado
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0,
    total: 0,
  });
  const [activeRoute, setActiveRoute] = useState(null);
  const [pendingRoutes, setPendingRoutes] = useState([]);
  const [error, setError] = useState(null);

  // Carregar dados
  useEffectAsync(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const myRoutes = await driverAssignmentService.getMyRoutes();

      // Calcular estatisticas
      const pending = myRoutes.filter((r) => r.assignmentStatus === ASSIGNMENT_STATUS.PENDING);
      const inProgress = myRoutes.filter((r) => r.status === 'executing');
      const completed = myRoutes.filter((r) => r.status === 'completed');

      setStats({
        pending: pending.length,
        inProgress: inProgress.length,
        completed: completed.length,
        total: myRoutes.length,
      });

      // Definir rota ativa (primeira em execucao)
      if (inProgress.length > 0) {
        setActiveRoute(inProgress[0]);
      }

      // Rotas pendentes de aceite
      setPendingRoutes(pending.slice(0, 3)); // Mostrar max 3
    } catch (err) {
      console.error('[DriverHomePage] Error:', err);
      setError('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handlers
  const handleNavigate = useCallback((stop) => {
    // Abrir navegacao externa (Google Maps, Waze, etc)
    const url = `https://www.google.com/maps/dir/?api=1&destination=${stop.lat || stop.latitude},${stop.lng || stop.longitude}`;
    window.open(url, '_blank');
  }, []);

  const handleViewRoute = useCallback((route) => {
    navigate(`/driver/routes/${route.id}`);
  }, [navigate]);

  const handleAcceptRoute = useCallback(async (route) => {
    try {
      await driverAssignmentService.acceptAssignment(route.id, route.assignedVehicle?.id);
      // Recarregar dados
      window.location.reload();
    } catch (err) {
      console.error('[DriverHomePage] Error accepting:', err);
    }
  }, []);

  const handleRejectRoute = useCallback((route) => {
    navigate(`/driver/routes/${route.id}/reject`);
  }, [navigate]);

  // Loading
  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rounded" height={100} sx={{ mb: 2 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <Skeleton variant="rounded" height={80} />
          <Skeleton variant="rounded" height={80} />
        </Box>
        <Skeleton variant="rounded" height={180} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Erro */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Saudacao */}
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            color: tokens?.text?.primary,
            fontWeight: 700,
            fontSize: '24px',
          }}
        >
          Ola! 👋
        </Typography>
        <Typography
          sx={{
            color: tokens?.text?.secondary,
            fontSize: '14px',
          }}
        >
          {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
        </Typography>
      </Box>

      {/* Rota ativa */}
      {activeRoute && (
        <Box sx={{ mb: 3 }}>
          <ActiveRouteCard
            route={activeRoute}
            onNavigate={handleNavigate}
            onViewDetails={() => handleViewRoute(activeRoute)}
            tokens={tokens}
          />
        </Box>
      )}

      {/* Estatisticas */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          mb: 3,
        }}
      >
        <StatCard
          icon={PendingIcon}
          label="Pendentes"
          value={stats.pending}
          color={tokens?.semantic?.warning}
          onClick={() => navigate('/driver/routes?status=pending')}
          tokens={tokens}
        />
        <StatCard
          icon={VehicleIcon}
          label="Em Rota"
          value={stats.inProgress}
          color={tokens?.accent?.primary}
          onClick={() => navigate('/driver/routes?status=executing')}
          tokens={tokens}
        />
        <StatCard
          icon={CompleteIcon}
          label="Concluidas"
          value={stats.completed}
          color={tokens?.semantic?.success}
          onClick={() => navigate('/driver/history')}
          tokens={tokens}
        />
        <StatCard
          icon={TaskIcon}
          label="Total Hoje"
          value={stats.total}
          color={tokens?.text?.secondary}
          onClick={() => navigate('/driver/routes')}
          tokens={tokens}
        />
      </Box>

      {/* Rotas pendentes de aceite */}
      {pendingRoutes.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon sx={{ color: tokens?.semantic?.warning, fontSize: 20 }} />
              <Typography
                sx={{
                  color: tokens?.text?.primary,
                  fontWeight: 600,
                  fontSize: '16px',
                }}
              >
                Aguardando Aceite
              </Typography>
            </Box>
            {pendingRoutes.length > 1 && (
              <Button
                size="small"
                onClick={() => navigate('/driver/routes?status=pending')}
                sx={{ color: tokens?.accent?.primary, textTransform: 'none' }}
              >
                Ver todas ({stats.pending})
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {pendingRoutes.map((route) => (
              <PendingRouteCard
                key={route.id}
                route={route}
                onAccept={handleAcceptRoute}
                onReject={handleRejectRoute}
                onViewDetails={() => handleViewRoute(route)}
                tokens={tokens}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Sem rotas */}
      {!activeRoute && pendingRoutes.length === 0 && (
        <Card
          sx={{
            backgroundColor: tokens?.surface?.raised,
            borderRadius: tokens?.radius?.lg,
            border: `1px solid ${tokens?.border?.subtle}`,
            textAlign: 'center',
            py: 6,
          }}
        >
          <VehicleIcon sx={{ fontSize: 64, color: tokens?.text?.tertiary, mb: 2 }} />
          <Typography
            sx={{
              color: tokens?.text?.secondary,
              fontSize: '16px',
              mb: 1,
            }}
          >
            Nenhuma rota atribuida
          </Typography>
          <Typography
            sx={{
              color: tokens?.text?.tertiary,
              fontSize: '14px',
            }}
          >
            Aguarde a atribuicao de novas rotas
          </Typography>
        </Card>
      )}
    </Box>
  );
};

export default DriverHomePage;
